import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetDoctorAttendingsWithAccountingsQuery } from '@/hooks/queries/use-get-doctor-attendings-with-accountings';
import useDebounce from '@/hooks/use-debounce';
import { doctorFullnameTitleTrad } from '@/utils/doctor';
import { SelectSearchFormOptions } from './select-search-form';

type SelectDoctorAttendingWithAccountingsSelectedValue = {
  id: string;
  label: string;
  officeId: string;
  doctorAttendingId: string;
  officeName: string;
};

export const useSelectSearchDoctorAttendingWithAccountingListViewModel = () => {
  const { t } = useTranslation();

  const [doctorAttendingSearch, setDoctorAttendingSearch] =
    useState<string>('');
  const debounceDoctorAttendingSearch = useDebounce(doctorAttendingSearch);

  const { data: doctorAttendingsResponse, isLoading: doctorAttendingsLoading } =
    useGetDoctorAttendingsWithAccountingsQuery({
      page: 1,
      per_page: 10,
      search: debounceDoctorAttendingSearch,
    });

  const doctorAttendingOptions = useMemo<
    SelectSearchFormOptions<SelectDoctorAttendingWithAccountingsSelectedValue>
  >(() => {
    if (!doctorAttendingsResponse) {
      return [];
    }

    return doctorAttendingsResponse.data.map((item) => {
      const fullnameTrad = doctorFullnameTitleTrad(item);

      const itemId = `${item.id}-${item.office.id}`;

      return {
        id: itemId,
        label: t(fullnameTrad.key, fullnameTrad.params),
        description: item.office.name,
        value: {
          id: itemId,
          officeId: item.office.id,
          doctorAttendingId: item.id,
          label: t(fullnameTrad.key, fullnameTrad.params),
          officeName: item.office.name,
        } satisfies SelectDoctorAttendingWithAccountingsSelectedValue,
      };
    });
  }, [doctorAttendingsResponse, doctorAttendingsLoading]);

  return {
    options: doctorAttendingOptions,
    search: debounceDoctorAttendingSearch,
    api: {
      setSearch: setDoctorAttendingSearch,
      getValuePlaceholder: (
        value?: SelectDoctorAttendingWithAccountingsSelectedValue,
      ) => {
        if (value) {
          return value.label;
        }

        return undefined;
      },
    },
  };
};
